import { useEffect, useId, useRef, useState } from "react";
import { collection, deleteDoc, doc, onSnapshot, runTransaction, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { dataCupomBrasilia, normalizarCodigoCupomLoja, statusCupomLoja, validarFormularioCupomLoja } from "../utils/cuponsLojaAdmin";
import "./cuponsLoja.css";

const novoFormulario = () => ({ codigo: "", tipo: "percentual", valor: "", valorMinimo: "", descontoMaximo: "", validade: "", ativo: true });
const moeda = (valor) => Number(valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const mostrarDesconto = (cupom) => cupom.tipo === "percentual" ? `${Number(cupom.valor).toLocaleString("pt-BR")}%` : moeda(cupom.valor);
const mostrarValidade = (valor) => {
  const data = dataCupomBrasilia(valor);
  return data ? data.split("-").reverse().join("/") : "Data inválida";
};

export default function CuponsLoja({ uid }) {
  const id = useId();
  const [cupons, setCupons] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroLista, setErroLista] = useState("");
  const [tentativa, setTentativa] = useState(0);
  const [formulario, setFormulario] = useState(null);
  const [editando, setEditando] = useState(null);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [salvando, setSalvando] = useState(false);
  const ocupado = useRef(false);
  const vivo = useRef(true);
  const formularioRef = useRef(null);
  const novoRef = useRef(null);

  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);
  useEffect(() => {
    if (!uid) { setCarregando(false); return undefined; }
    setCarregando(true);
    setErroLista("");
    return onSnapshot(collection(db, "users", uid, "cupons_loja"), (snapshot) => {
      setCupons(snapshot.docs.map((item) => ({ ...item.data(), id: item.id }))
        .sort((a, b) => String(a.codigo || a.id).localeCompare(String(b.codigo || b.id), "pt-BR")));
      setCarregando(false);
    }, () => {
      setErroLista("Não foi possível carregar os cupons. Confira sua conexão e tente novamente.");
      setCarregando(false);
    });
  }, [uid, tentativa]);

  const abrirFormulario = (cupom = null) => {
    setErro(""); setSucesso(""); setEditando(cupom?.id || null);
    setFormulario(cupom ? {
      codigo: cupom.codigo || cupom.id, tipo: cupom.tipo, valor: String(cupom.valor),
      valorMinimo: String(cupom.valorMinimo ?? ""),
      descontoMaximo: String(cupom.descontoMaximo ?? ""),
      validade: dataCupomBrasilia(cupom.expiraEm), ativo: cupom.ativo === true,
    } : novoFormulario());
  };
  const fecharFormulario = () => { setFormulario(null); setEditando(null); setErro(""); novoRef.current?.focus(); };
  const formularioAberto = formulario !== null;
  useEffect(() => {
    if (!formularioAberto) return;
    const campo = formularioRef.current?.querySelector("input:not([readonly])");
    campo?.focus({ preventScroll: true });
    formularioRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [formularioAberto, editando]);
  const alterar = (campo, valor) => { setFormulario((atual) => ({ ...atual, [campo]: valor })); setErro(""); };

  const salvar = async (evento) => {
    evento.preventDefault();
    if (ocupado.current || !uid) return;
    const resultado = validarFormularioCupomLoja(formulario);
    if (resultado.erro) { setErro(resultado.erro); return; }
    ocupado.current = true; setSalvando(true); setErro(""); setSucesso("");
    try {
      const dados = resultado.dados;
      const referencia = doc(db, "users", uid, "cupons_loja", editando || dados.codigo);
      await runTransaction(db, async (transacao) => {
        const atual = await transacao.get(referencia);
        if (!editando && atual.exists()) throw new Error("codigo-existente");
        if (editando && !atual.exists()) throw new Error("cupom-ausente");
        transacao.set(referencia, {
          ...dados, atualizadoEm: serverTimestamp(),
          ...(!editando ? { criadoEm: serverTimestamp() } : {}),
        }, { merge: true });
      });
      if (!vivo.current) return;
      setSucesso(`Cupom ${dados.codigo} ${editando ? "atualizado" : "criado"}.`);
      fecharFormulario();
    } catch (falha) {
      if (!vivo.current) return;
      setErro(falha.message === "codigo-existente" ? "Este código já existe. Edite o cupom da lista ou escolha outro código."
        : falha.message === "cupom-ausente" ? "Este cupom não está mais disponível. Atualize a lista e tente novamente."
        : "Não foi possível salvar o cupom. Confira sua conexão e tente novamente.");
    } finally {
      ocupado.current = false;
      if (vivo.current) setSalvando(false);
    }
  };

  const excluirCupom = async (cupom) => {
    if (ocupado.current || !uid) return;
    if (!window.confirm(`Excluir o cupom ${cupom.codigo || cupom.id}? Pedidos que já usaram esse cupom não são alterados.`)) return;
    ocupado.current = true; setSalvando(true); setErro(""); setSucesso("");
    try {
      await deleteDoc(doc(db, "users", uid, "cupons_loja", cupom.id));
      if (vivo.current) setSucesso(`Cupom ${cupom.codigo || cupom.id} excluído.`);
    } catch {
      if (vivo.current) setErro("Não foi possível excluir o cupom. Tente novamente.");
    } finally {
      ocupado.current = false;
      if (vivo.current) setSalvando(false);
    }
  };

  const alternarAtivo = async (cupom) => {
    if (ocupado.current || !uid) return;
    if (!cupom.ativo && statusCupomLoja({ ...cupom, ativo: true }) === "Expirado") {
      abrirFormulario(cupom);
      setErro("Atualize a validade antes de ativar este cupom.");
      return;
    }
    ocupado.current = true; setSalvando(true); setErro(""); setSucesso("");
    try {
      await updateDoc(doc(db, "users", uid, "cupons_loja", cupom.id), {
        ativo: !cupom.ativo, atualizadoEm: serverTimestamp(),
      });
      if (vivo.current) setSucesso(`Cupom ${cupom.codigo} ${cupom.ativo ? "pausado" : "ativado"}.`);
    } catch {
      if (vivo.current) setErro("Não foi possível alterar o cupom. Tente novamente.");
    } finally {
      ocupado.current = false;
      if (vivo.current) setSalvando(false);
    }
  };

  return (
    <section className="loja-coupons" aria-labelledby={`${id}-titulo`}>
      <div className="loja-coupons-heading">
        <div><span className="loja-coupons-eyebrow">PROMOÇÕES DA SUA VITRINE</span><h2 id={`${id}-titulo`}>Cupons de desconto</h2></div>
        <button ref={novoRef} type="button" className="loja-coupons-primary" onClick={() => abrirFormulario()} disabled={salvando || formularioAberto || !uid}>+ Novo cupom</button>
      </div>
      <p className="loja-coupons-description">Crie um código para seus clientes usarem no carrinho desta loja. O desconto vale sobre os produtos, sem incluir a entrega.</p>
      {sucesso && <p className="loja-coupons-feedback success" role="status">{sucesso}</p>}
      {erro && <p className="loja-coupons-feedback error" role="alert">{erro}</p>}
      {formulario && (
        <form ref={formularioRef} className="loja-coupons-form" onSubmit={salvar} noValidate aria-label={editando ? "Editar cupom" : "Criar cupom"}>
          <h3>{editando ? "Editar cupom" : "Novo cupom"}</h3>
          <fieldset disabled={salvando}>
            <div className="loja-coupons-fields">
              <div className="loja-coupons-wide"><label htmlFor={`${id}-codigo`}>Código do cupom</label>
                <input id={`${id}-codigo`} value={formulario.codigo} onChange={(e) => alterar("codigo", normalizarCodigoCupomLoja(e.target.value))} placeholder="Ex.: BEMVINDO10" maxLength={40} autoCapitalize="characters" autoComplete="off" spellCheck={false} readOnly={!!editando} aria-describedby={`${id}-codigo-ajuda`} />
                <small id={`${id}-codigo-ajuda`}>{editando ? "O código não muda. Para outro código, crie um novo cupom." : "De 3 a 40 caracteres. Sem espaços nem acentos."}</small>
              </div>
              <div><label htmlFor={`${id}-tipo`}>Tipo de desconto</label>
                <select id={`${id}-tipo`} value={formulario.tipo} onChange={(e) => alterar("tipo", e.target.value)}><option value="percentual">Percentual (%)</option><option value="fixo">Valor em reais (R$)</option></select>
              </div>
              <div><label htmlFor={`${id}-valor`}>{formulario.tipo === "percentual" ? "Desconto (%)" : "Desconto (R$)"}</label>
                <input id={`${id}-valor`} type="text" inputMode="decimal" value={formulario.valor} onChange={(e) => alterar("valor", e.target.value)} placeholder={formulario.tipo === "percentual" ? "10" : "15,00"} />
              </div>
              <div className="loja-coupons-wide"><label htmlFor={`${id}-minimo`}>Compra mínima (R$)</label>
                <input id={`${id}-minimo`} type="text" inputMode="decimal" value={formulario.valorMinimo} onChange={(e) => alterar("valorMinimo", e.target.value)} placeholder="Ex.: 50,00" aria-describedby={`${id}-minimo-ajuda`} />
                <small id={`${id}-minimo-ajuda`}>Ex.: 50 permite compras a partir de R$ 50,00 em produtos, antes do desconto e sem entrega. Deixe vazio ou zero para não exigir mínimo.</small>
              </div>
              <div className="loja-coupons-wide"><label htmlFor={`${id}-maximo`}>Desconto máximo (R$)</label>
                <input id={`${id}-maximo`} type="text" inputMode="decimal" value={formulario.descontoMaximo} onChange={(e) => alterar("descontoMaximo", e.target.value)} placeholder="Ex.: 30,00" aria-describedby={`${id}-maximo-ajuda`} />
                <small id={`${id}-maximo-ajuda`}>Ex.: 30 limita o desconto deste cupom a R$ 30,00, mesmo em percentual. Deixe vazio ou zero para não limitar.</small>
              </div>
              <div className="loja-coupons-wide"><label htmlFor={`${id}-validade`}>Válido até</label>
                <input id={`${id}-validade`} type="date" min={dataCupomBrasilia(new Date().toISOString())} value={formulario.validade} onChange={(e) => alterar("validade", e.target.value)} aria-describedby={`${id}-validade-ajuda`} />
                <small id={`${id}-validade-ajuda`}>Encerra às 23h59 da data escolhida, no horário de Brasília.</small>
              </div>
            </div>
            <label className="loja-coupons-checkbox" htmlFor={`${id}-ativo`}><input id={`${id}-ativo`} type="checkbox" checked={formulario.ativo} onChange={(e) => alterar("ativo", e.target.checked)} /><span>Cupom ativo para os clientes</span></label>
            {Number(String(formulario.valor).replace(",", ".")) > 0 && <p className="loja-coupons-preview">Seu cliente recebe <strong>{mostrarDesconto({ ...formulario, valor: Number(String(formulario.valor).replace(",", ".")) })} de desconto</strong>{formulario.tipo === "fixo" ? ", limitado ao subtotal dos produtos." : " nos produtos do carrinho."}{Number(String(formulario.descontoMaximo).replace(",", ".")) > 0 && ` Nunca passa de ${moeda(Number(String(formulario.descontoMaximo).replace(",", ".")))}.`}</p>}
            <div className="loja-coupons-form-actions"><button type="button" className="loja-coupons-secondary" onClick={fecharFormulario}>Cancelar</button><button type="submit" className="loja-coupons-primary">{salvando ? "Salvando…" : "Salvar cupom"}</button></div>
          </fieldset>
        </form>
      )}
      {carregando ? <p className="loja-coupons-empty" role="status">Carregando cupons…</p> : erroLista ? (
        <div className="loja-coupons-empty"><p role="alert">{erroLista}</p><button type="button" className="loja-coupons-secondary" onClick={() => setTentativa((valor) => valor + 1)}>Tentar novamente</button></div>
      ) : !cupons.length ? <div className="loja-coupons-empty"><strong>Seu próximo cliente pode ganhar um desconto.</strong><p>Cadastre o primeiro cupom e compartilhe o código com quem você quiser.</p></div> : (
        <ul className="loja-coupons-list" aria-label="Cupons cadastrados">
          {cupons.map((cupom) => {
            const status = statusCupomLoja(cupom);
            return <li key={cupom.id} className="loja-coupon-item">
              <div className="loja-coupon-main"><div className="loja-coupon-code"><strong>{cupom.codigo || cupom.id}</strong><span className={`loja-coupon-status ${status === "Ativo" ? "active" : ""}`}>{status}</span></div><p><strong>{mostrarDesconto(cupom)} de desconto</strong><span>{cupom.valorMinimo > 0 ? `Compra mínima: ${moeda(cupom.valorMinimo)}` : "Sem compra mínima"}</span>{cupom.descontoMaximo > 0 && <span>Desconto até {moeda(cupom.descontoMaximo)}</span>}<span>Válido até {mostrarValidade(cupom.expiraEm)}</span></p></div>
              <div className="loja-coupon-actions"><button type="button" className="loja-coupons-secondary" disabled={salvando || formularioAberto} onClick={() => abrirFormulario(cupom)} aria-label={`Editar cupom ${cupom.codigo}`}>Editar</button><button type="button" className="loja-coupons-secondary" disabled={salvando || formularioAberto} onClick={() => alternarAtivo(cupom)} aria-label={`${cupom.ativo ? "Pausar" : "Ativar"} cupom ${cupom.codigo}`}>{cupom.ativo ? "Pausar" : "Ativar"}</button><button type="button" className="loja-coupons-secondary loja-coupons-danger" disabled={salvando || formularioAberto} onClick={() => excluirCupom(cupom)} aria-label={`Excluir cupom ${cupom.codigo}`}>Excluir</button></div>
            </li>;
          })}
        </ul>
      )}
    </section>
  );
}
